/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useTranslation } from "@i18n";
import folderImg from "../../assets/icons/svg/folder.svg";
import folderLockImg from "../../assets/icons/svg/folderlock.svg";
import spaceImg from "../../assets/icons/svg/spaces.svg";

interface UseFileManagerLeftSideTreeProps {
  setSelectedFolder: Function;
  selectedFolder: any;
  fileManagerData: any;
  onFileLoad: Function;
}

function useFileManagerLeftSideTree({
  setSelectedFolder,
  selectedFolder,
  fileManagerData,
  onFileLoad,
}: UseFileManagerLeftSideTreeProps) {
  const { t } = useTranslation();

  // State
  const [folderTreeData, setFolderTreeData] = useState<any>([]);
  const [folderCollapsedState, setFolderCollapsedState] = useState<any>({});
  const [isFileClicked, setIsFileClicked] = useState<boolean>(false);

  // Refs
  const tempRef = useRef<any>(null);

  // Helper functions
  // `rootParentId` seeds the recursion: "0" for a single space root, `null` for the project tree where
  // each Space is a top-level node (parentId === null). `startLevel` is the tree depth of that first
  // layer (0 for project Space roots; 1 for single-space folders that sit under a separate depth-0 root).
  function sortWithHierarchy(arr: any, rootParentId: any = "0", startLevel = 1) {
    const byParent = arr.reduce((acc: any, item: any) => {
      if (!acc[item.parentId]) {
        acc[item.parentId] = [];
      }
      acc[item.parentId].push(item);
      return acc;
    }, {});

    function sortRecursive(parentId: any, level: number): any[] {
      const children = byParent[parentId] || [];
      // Default Space pins to the top of its level; everything else is alphabetical. (Only Space roots
      // carry isDefault, so folders fall straight through to the name sort.)
      children.sort((a: any, b: any) => {
        const defaultDiff = (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
        return defaultDiff || a.name.localeCompare(b.name);
      });
      // Stamp the real tree depth so the SlickGrid tree (levelPropName: "treeLevel") indents each row
      // correctly, rather than relying on the `path` field that isn't populated on the raw data yet.
      return children.flatMap((child: any) => [{ ...child, __depth: level }, ...sortRecursive(child.id, level + 1)]);
    }

    return sortRecursive(rootParentId, startLevel);
  }

  // Handlers
  const handleOnClick = (args: any, treeViewRef: any) => {
    setIsFileClicked(true);
    const rowIndex = args.detail.args.row;
    const clickedRow = treeViewRef.current.slickGrid.getDataItem(rowIndex);
    setFolderCollapsedState((prev: any) => {
      prev[clickedRow.id] = !prev[clickedRow.id];
      return prev;
    });
    setSelectedFolder(clickedRow);
  };

  const titleFormatter = (data: any, value: any, a: any, b: any, args: any) => {
    let iconSrc = folderImg;
    if (!args?.permission?.read) {
      iconSrc = folderLockImg;
    }
    if (args.id == "0" || args.isSpaceRoot) {
      iconSrc = spaceImg;
    }
    const defaultChip = args.isDefault
      ? `<span style="margin-inline-start:0.375rem;font-size:0.625rem;line-height:1;padding:0.375rem 0.75rem;border-radius:0.625rem;background:#666a84;color:#ffffff;font-weight:600;white-space:nowrap;">${t("Default")}</span>`
      : "";
    // Escape the (user-controlled) name — this string is rendered as raw HTML by the SlickGrid formatter,
    // so an unescaped name like `<img src=x onerror=...>` would execute.
    return `<span style="display:flex;align-items:center;gap:0.375rem; ">
      <img src="${iconSrc}" alt="icon" style="width:1.25rem;height:1.25rem;" />
      ${_.escape(args.name ?? "")}
      ${defaultChip}
    </span>
  `;
  };

  const collapseParentNodesWithoutChildData = (treeViewRef: any) => {
    setTimeout(() => {
      treeViewRef.current?.treeDataService.toggleTreeDataCollapse(false);
    }, 0);
  };

  const syncSelectedFolder = (treeViewRef: any) => {
    if (treeViewRef.current?.dataView) {
      const dataView = treeViewRef.current?.dataView;
      const rowIndex = dataView.getRowById(selectedFolder.id);
      treeViewRef.current.slickGrid.setSelectedRows([rowIndex]);

      if (selectedFolder.isBulkFetch) {
        const index = folderTreeData.findIndex((d: any) => d.id == selectedFolder.id);
        const node = treeViewRef.current.slickGrid.getCellNode(index, 0);
        const rowNode = node?.closest(".slick-row");
        if (rowNode) {
          rowNode.scrollIntoView({ behavior: "auto", block: "center" });
        }
      }
    }
  };

  // Effects
  useEffect(() => {
    const allItems =
      _.cloneDeep(fileManagerData.files)?.filter(
        (item: any) =>
          ["RESOURCE_TYPE_TRANSMITTAL", "RESOURCE_TYPE_FOLDER"].includes(item.resourceType) && item.id != "0",
      ) || [];

    onFileLoad(allItems);

    // Project ("Folders") mode is detected structurally: the data carries one synthetic Space-root node
    // per Space (parentId === null). There is then no single synthetic root — each Space IS a root.
    const isProjectTree = allItems.some((item: any) => item.isSpaceRoot);

    // `__depth` is the real tree depth stamped by sortWithHierarchy (root = 0, its folders = 1, …).
    const decorate = (d: any) => {
      const level = d.__depth ?? 0;
      return {
        ...d,
        indentLevel: level,
        hasChild: !!d.hasChild,
        __hasChildren: !!d.hasChild,
        treeLevel: level,
        __collapsed: folderCollapsedState[d.id],
        isExpanded: !folderCollapsedState[d.id],
      };
    };

    if (isProjectTree) {
      // Space roots sit at depth 0; their folders/sub-folders nest beneath (1, 2, …).
      const updatedData: any = sortWithHierarchy(allItems, null, 0).map(decorate);
      setFolderTreeData(updatedData);
      return;
    }

    const root: any = {
      ...fileManagerData.cwd,
      nodeId: fileManagerData.cwd?.id || "cwd",
      id: "0",
      indentLevel: 0,
      parentId: null,
      treeLevel: 0,
      hasChild: true,
      __hasChildren: true,
      __collapsed: false,
      isExpanded: true,
    };

    // The single synthetic root is depth 0, so its folders start at depth 1.
    const updatedData: any = sortWithHierarchy(allItems, "0", 1).map(decorate);

    setFolderTreeData([root, ...updatedData]);
  }, [folderCollapsedState]);

  useEffect(() => {
    const allItems =
      fileManagerData.files?.filter(
        (item: any) =>
          ["RESOURCE_TYPE_TRANSMITTAL", "RESOURCE_TYPE_FOLDER"].includes(item.resourceType) && item.id != "0",
      ) || [];

    const isProjectTree = allItems.some((item: any) => item.isSpaceRoot);
    // The Space chosen as cwd (default Space, else the first accessible one alphabetically).
    const defaultSpaceId = fileManagerData.cwd?.id;

    setFolderCollapsedState((prev: any) => {
      const newObj = { ...prev };
      allItems.map((f: any) => {
        if (isProjectTree) {
          // Project ("Folders") mode is eager, so every node has children loaded. Start with only the
          // cwd Space expanded and every other Space + all folders collapsed; set once per node so the
          // user's manual expand/collapse survives data refreshes.
          if (typeof newObj[f.id] == "undefined") {
            newObj[f.id] = !(f.isSpaceRoot && f.id === defaultSpaceId);
          }
        } else if (typeof newObj[f.id] == "undefined" || !isFileClicked) {
          const childItem = allItems.find((item: any) => item.parentId == f.id);
          newObj[f.id] = !childItem;
        }
      });
      return newObj;
    });
    setIsFileClicked(false);
  }, [fileManagerData.files]);

  // Deep-link refresh restore: when a nested folder is restored (updateCurrentDir stamps isBulkFetch), expand
  // its Space root + ancestor chain + the folder itself so it's revealed and highlighted in the tree. Gated
  // on isBulkFetch, so normal clicks and single-space mode are untouched (they never set it).
  useEffect(() => {
    if (!selectedFolder?.isBulkFetch) return;
    const expandIds = [
      String(selectedFolder.spaceId ?? ""),
      ...(selectedFolder.parentIdArray || []).map((id: any) => String(id)),
      String(selectedFolder.id ?? ""),
    ].filter((id: string) => id && id !== "0");
    if (!expandIds.length) return;
    setFolderCollapsedState((prev: any) => {
      const next = { ...prev };
      expandIds.forEach((id: string) => {
        next[id] = false;
      });
      return next;
    });
  }, [selectedFolder?.id]);

  return {
    // State
    folderTreeData,
    setFolderTreeData,
    folderCollapsedState,
    setFolderCollapsedState,

    // Refs
    tempRef,

    // Handlers
    sortWithHierarchy,
    handleOnClick,
    titleFormatter,
    collapseParentNodesWithoutChildData,
    syncSelectedFolder,

    // Props passthrough
    selectedFolder,
  };
}

export default useFileManagerLeftSideTree;
